import { CountryAlpha3Code } from '../countries/countries';
import { APP_STAGE, AppStage } from '../frontendConfig';
import { getOverriddenThemeConfig } from './themeConfigEnvironmentOverrides';
import { Theme, ThemePath, THEMES } from './themes';

const LOCAL_STORAGE_THEME = 'Theme';

// See table tblClientOwnerSource AwcData
export const SOURCES = {
  AsbPublic: 'asbpublic',
  CmcInvestAlphaPublic: 'cmcinvestalphapublic',
  CmcPublic: 'cmcpublic',
  CmcInvestSingaporePublic: 'cmcinvestsingaporepublic',
  BendigoPublic: 'bendigopublic',
  StGeorgePublic: 'stgeorgepublic',
  AdvantagePublic: 'advantagepublic',
  EBrokingPublic: 'ebrokingpublic',
  RivkinPublic: 'rivkinpublic',
  WestpacPublic: 'westpacpublic'
} as const;

type Source = (typeof SOURCES)[keyof typeof SOURCES];

type DefaultProfileSettings = Record<string, any>;

type MobileAppLinks = {
  appStore: string;
  googlePlayStore: string;
};

type IContactNumber = {
  domestic: string;
  international?: string;
};

type FalconPreviewConfig = {
  previewUrl: string;
  previewCompactUrl: string;
};

/**
 * Add other video types as needed
 */
type VideoConfig = {
  platformGuide?: string;
};

export type ThemeConfig = {
  source: Source;
  themePath: ThemePath;
  defaultProfileSettings: DefaultProfileSettings;
  mobileAppLinks: MobileAppLinks;
  onboardingUrl: string;
  serviceEmail: string;
  serviceNumber: IContactNumber;
  websiteUrl: string;
  contactUsUrl: string;
  proUrl?: string;
  stockbrokingPlatformUrl: string;
  docTitle: string;
  appName: string;
  falconPreviewConfig?: FalconPreviewConfig;
  videos?: VideoConfig;
};

const CmcThemeConfig: ThemeConfig = {
  source: SOURCES.CmcPublic,
  themePath: `au`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/cmc-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/au/create-log-in`,
  serviceNumber: { domestic: `1300 360 071` },
  serviceEmail: `brokingservice@cmcmarkets.com.au`,
  websiteUrl: `https://www.cmcmarkets.com/en-au/`,
  // themePath: `au` //TODO: not set for now
  contactUsUrl: `https://www.cmcmarkets.com/en-au/contact-us`,
  proUrl: 'https://pro.cmcmarketsinvest.com',
  stockbrokingPlatformUrl: '',
  docTitle: 'CMC Markets Invest',
  appName: 'CMC Invest',
  videos: {
    platformGuide: 'https://www.youtube.com/embed/WxsjkzHc1eA?autoplay=1'
  }
};

const AsbThemeConfig: ThemeConfig = {
  source: SOURCES.AsbPublic,
  themePath: `asb`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.NZL
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/cmc-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/au/create-log-in`,
  serviceNumber: { domestic: `1300 360 071` },
  serviceEmail: `brokingservice@cmcmarkets.com.au`,
  websiteUrl: `https://www.cmcmarkets.com/en-au/`,
  contactUsUrl: `https://www.asb.co.nz/contact-us`,
  stockbrokingPlatformUrl: '',
  docTitle: 'ASB Securities',
  appName: 'ASB Securities'
};

const CMCSingaporeConfig: ThemeConfig = {
  source: SOURCES.CmcInvestSingaporePublic,
  themePath: `sg`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.SGP
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/sg/app/cmc-markets-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/sg/create-log-in`,
  serviceNumber: { domestic: `1800 559 6000`, international: `+65 6559 6000` },
  serviceEmail: `support@cmcinvest.sg`,
  websiteUrl: `https://www.cmcinvest.sg`,
  contactUsUrl: `https://www.cmcinvest.sg/contact-us`,
  stockbrokingPlatformUrl: '',
  docTitle: 'CMC Markets Invest',
  appName: 'CMC Invest'
};

const AdvantageConfig: ThemeConfig = {
  source: SOURCES.AdvantagePublic,
  themePath: `partners`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/advantage-trader/id1631458719`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcadvantage.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/au/create-log-in`,
  serviceNumber: { domestic: `1300 557 561` },
  serviceEmail: `brokingservice@cmcmarkets.com.au`,
  websiteUrl: `https://www.cmcmarkets.com/en-au/`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  proUrl: 'https://adv-pro.cmcmarketsstockbroking.com.au',
  docTitle: 'The Advantage Platform',
  appName: 'Advantage',
  falconPreviewConfig: {
    previewUrl: '/l/advantage/preview.png',
    previewCompactUrl: '/l/advantage/700/preview.png'
  }
};

const BendigoConfig: ThemeConfig = {
  source: SOURCES.BendigoPublic,
  themePath: `bendigo`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/bendigo-invest-direct/id1462721295`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.bendigobank.mobileios.BendigoInvestDirect`
  },
  onboardingUrl: `https://trading.bendigoinvestdirect.com.au/apply#/start`,
  serviceNumber: { domestic: `1300 788 982` },
  serviceEmail: `info@bendigoinvestdirect.com.au`,
  websiteUrl: `https://www.bendigobank.com.au/personal/investing/share-trading/`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  proUrl: 'https://pro.bendigoinvestdirect.com.au',
  docTitle: 'Bendigo Invest Direct',
  appName: 'Bendigo Invest Direct',
  falconPreviewConfig: {
    previewUrl: '/l/bendigo/preview.png',
    previewCompactUrl: '/l/bendigo/700/preview.png'
  }
};

const CmcAlphaConfig: ThemeConfig = {
  source: SOURCES.CmcInvestAlphaPublic,
  themePath: `alpha`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/cmc-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/au/create-log-in`,
  serviceNumber: { domestic: `1300 080 557` },
  serviceEmail: `premium@cmcmarkets.com.au`,
  websiteUrl: `https://www.cmcmarkets.com/en-au/alpha/invest`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  docTitle: 'CMC Markets Alpha',
  appName: 'CMC Invest',
  falconPreviewConfig: {
    previewUrl: '/l/alpha/preview.png',
    previewCompactUrl: '/l/alpha/700/preview.png'
  },
  videos: {
    platformGuide: 'https://www.youtube.com/embed/WxsjkzHc1eA?autoplay=1'
  }
};

const EBrokingConfig: ThemeConfig = {
  source: SOURCES.EBrokingPublic,
  themePath: `ebroking`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/ebroking/id1631480954`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcebroking.android`
  },
  onboardingUrl: `https://www.esuperfund.com.au/`,
  serviceNumber: { domestic: `1300 799 800` },
  serviceEmail: `brokerservices@ebroking.com.au`,
  websiteUrl: `https://www.esuperfund.com.au/`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  docTitle: 'EBROKING',
  appName: 'EBROKING',
  falconPreviewConfig: {
    previewUrl: '/l/ebroking/preview.png',
    previewCompactUrl: '/l/ebroking/700/preview.png'
  }
};

const RivkinConfig: ThemeConfig = {
  source: SOURCES.RivkinPublic,
  themePath: `rivkin`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/cmc-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://rivkin.com.au/trading/`,
  serviceNumber: { domestic: `1300 360 071` },
  serviceEmail: `brokingservice@cmcmarkets.com.au`,
  websiteUrl: `https://rivkin.com.au/members/`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  docTitle: 'Rivkin Securities Pty Ltd',
  appName: 'CMC Invest',
  falconPreviewConfig: {
    previewUrl: '/l/rivkin/preview.png',
    previewCompactUrl: '/l/rivkin/700/preview.png'
  }
};

const StGeorgeConfig: ThemeConfig = {
  source: SOURCES.StGeorgePublic,
  themePath: `stgeorge`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/st-george-directshares/id1438804929`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=stgeorge.mobile.stockbroking.onlinetrading`
  },
  onboardingUrl: `https://trading.directshares.com.au/apply/join`,
  serviceNumber: { domestic: `1300 133 500` },
  serviceEmail: `service@directshares.com.au`,
  websiteUrl: `https://www.stgeorge.com.au/personal/investments/directshares-online-broking`,
  contactUsUrl: `/trading/Support/ContactUs`,
  stockbrokingPlatformUrl: '',
  proUrl: 'https://pro.directshares.com.au',
  docTitle: 'Directshares',
  appName: 'St.George Directshares',
  falconPreviewConfig: {
    previewUrl: '/l/stgeorge/preview.png',
    previewCompactUrl: '/l/stgeorge/700/preview.png'
  }
};

const WestpacThemeConfig: ThemeConfig = {
  source: SOURCES.WestpacPublic,
  themePath: `westpac`,
  defaultProfileSettings: {
    UiSelectedCountryIsoCode: CountryAlpha3Code.AUS
  },
  mobileAppLinks: {
    appStore: `https://apps.apple.com/au/app/cmc-invest/id1559149606`,
    googlePlayStore: `https://play.google.com/store/apps/details?id=com.cmcmarketsstockbroking.android`
  },
  onboardingUrl: `https://signup.invest.cmcmarkets.com.au/au/create-log-in`,
  serviceNumber: { domestic: `1300 360 071` },
  serviceEmail: `brokingservice@cmcmarkets.com.au`,
  websiteUrl: `https://www.cmcmarkets.com/en-au/`,
  contactUsUrl: `https://www.westpac.com.au/contact-us`,
  stockbrokingPlatformUrl: '',
  docTitle: 'Westpac Share Trading',
  appName: 'Westpac Share Trading'
};

export const themeConfig: Partial<Record<Theme, ThemeConfig>> = {
  [THEMES.Advantage]: AdvantageConfig,
  [THEMES.Bendigo]: BendigoConfig,
  [THEMES.CMC]: CmcThemeConfig,
  [THEMES.CMCAlpha]: CmcAlphaConfig,
  [THEMES.CMCSingapore]: CMCSingaporeConfig,
  [THEMES.eBroking]: EBrokingConfig,
  [THEMES.Rivkin]: RivkinConfig,
  [THEMES.StGeorge]: StGeorgeConfig,
  [THEMES.ASB]: AsbThemeConfig,
  [THEMES.Westpac]: WestpacThemeConfig
};

export const getThemeConfig = (theme: Theme = THEMES.CMC, environment: AppStage = APP_STAGE.Prod): ThemeConfig =>
  getOverriddenThemeConfig(themeConfig[theme] ?? themeConfig[THEMES.CMC]!, environment);

/**
 * get brand/theme based on the document path.
 * If not possible, use local storage or default to CMC Australia
 *
 * @param document HTML react
 * @returns theme
 */
//
export const getDocumentTheme = (document: Document): Theme => {
  const pathname = document.location.pathname;
  /// for ephemeral environment /webinvest-frontend/v/331/rivkin/login
  const brandPath = pathname.includes(`/v/`) ? pathname.split('/')[4] : pathname.split('/')[1];

  for (const [theme, config] of Object.entries(themeConfig)) {
    if (config && config.themePath === brandPath) {
      return theme as Theme;
    }
  }
  // Theme not found in URL, use cookie or default to CMC Australia
  const latestTheme = localStorage.getItem(LOCAL_STORAGE_THEME);
  return (latestTheme as Theme) || THEMES.CMC;
};
